/** 오늘 — what to look at first (S1).
 *
 *  Four tiles, today's and tomorrow's posts on a timeline with the actions to
 *  send one now or take it down, the list of things that are quietly wrong,
 *  and — folded away — the desk's numbers per app. Replaces the old Dashboard;
 *  everything it showed lives here: the tiles (issued today and live apps in
 *  the desk summary), the "지금 봐야 할 것" list, the per-app table, the
 *  accounts with their voice, and the request counts. The build number is in
 *  the sidebar footer. */

import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { CalendarClock, ChevronDown } from 'lucide-react'
import type { DeskStat, RedeemAppWithStats, ScheduleItem, Target } from '@/api/client'
import { TOKEN_WARN_DAYS } from '@/lib/attention'
import { useDeskStats, useDraftCounts, useRedeemApps, useRequests, useSchedule, useTargets } from '@/lib/queries'
import { TONE_CLASS, statusOf } from '@/lib/status'
import { formatShort } from '@/lib/time'
import { cn } from '@/lib/utils'
import {
  ButtonLink,
  DataTable,
  EmptyState,
  PageHeader,
  PlatformMark,
  StatTile,
  StatusDot,
  TimelineList,
  useAttentionItems,
  When,
  type Column,
} from '@/ui'
import type { AttentionItem } from '@/lib/attention'
import { ItemRow } from '../schedule/ScheduleList'
import { channelLabel, itemTime, publishingFor, STUCK_MINUTES, usePostCommands, whereLabel } from '../schedule/shared'

const DAY_MS = 86_400_000
const LOW_POOL = 25

export interface Note {
  key: string
  tone: 'failed' | 'publishing'
  text: string
  to: string
}

export interface NoteInputs {
  apps: RedeemAppWithStats[]
  stats: DeskStat[]
  targets: Target[]
  failed: ScheduleItem[]
  publishing: ScheduleItem[]
  /** Scheduled desk posts, whatever the day. */
  scheduledDesk: ScheduleItem[]
  now: Date
}

/** The things that go wrong without anyone looking: a pool that ran dry, a
 *  request nobody answered, a post that failed at 3am or stalled mid-publish,
 *  a token about to lapse, an account with no voice, a Reddit post going out
 *  without a flair. Failures first. */
export function todayNotes({ apps, stats, targets, failed, publishing, scheduledDesk, now }: NoteInputs): Note[] {
  const out: Note[] = []
  const live = apps.filter((r) => r.app.status === 'active')

  for (const r of live.filter((x) => x.stats.available === 0)) {
    out.push({ key: `dry:${r.app.id}`, tone: 'failed', text: `${r.app.name}: 가동 중인데 코드 풀이 비었다`, to: `/redeem/${r.app.id}` })
  }
  const failedOpen = stats.reduce((n, s) => n + s.failed_open, 0)
  if (failedOpen > 0) {
    out.push({ key: 'failed-requests', tone: 'failed', text: `발송 실패로 멈춘 요청 ${failedOpen}건`, to: '/redeem/requests?state=failed' })
  }
  for (const it of publishing.filter((x) => (publishingFor(x, now) ?? 0) > STUCK_MINUTES)) {
    out.push({
      key: `stuck:${it.kind}-${it.id}`,
      tone: 'failed',
      text: `${whereLabel(it)} 글이 게시 중에서 멈췄다`,
      to: '/schedule?status=publishing',
    })
  }
  for (const it of failed) {
    out.push({
      key: `failed:${it.kind}-${it.id}`,
      tone: 'failed',
      text: `${whereLabel(it)} 게시 실패 — ${it.error_msg || '사유 없음'}`,
      to: `/schedule?status=failed&source=${it.kind === 'redeem' ? 'redeem' : 'campaign'}`,
    })
  }
  for (const t of targets.filter((x) => x.status === 'active' && x.token_expires_at)) {
    const days = Math.floor((new Date(t.token_expires_at!).getTime() - now.getTime()) / DAY_MS)
    if (Number.isNaN(days) || days >= TOKEN_WARN_DAYS) continue
    out.push({
      key: `token:${t.id}`,
      tone: days < 2 ? 'failed' : 'publishing',
      text: `@${t.name} Threads 토큰이 ${days < 0 ? '만료됐다' : days === 0 ? '오늘 만료된다' : `${days}일 뒤 만료된다`}`,
      to: '/targets',
    })
  }
  for (const r of live.filter((x) => x.stats.available > 0 && x.stats.available < LOW_POOL)) {
    out.push({ key: `low:${r.app.id}`, tone: 'publishing', text: `${r.app.name}: 남은 코드 ${r.stats.available}장`, to: `/redeem/${r.app.id}` })
  }
  // A running app with sending off moves a request only when a person does:
  // its waiting requests are named per app (Dazzle Cam, 2026-10-18, read
  // 가동 as sending while two requests waited).
  const manual = new Set(live.filter((r) => r.app.auto_handout !== 1 && !r.app.ended_at).map((r) => r.app.id))
  for (const s of stats.filter((x) => manual.has(x.app_id) && x.pending_new > 0)) {
    out.push({
      key: `manual:${s.app_id}`,
      tone: 'publishing',
      text: `${s.name}: 자동 발송이 꺼져 있어 요청 ${s.pending_new}건이 사람을 기다린다`,
      to: `/redeem/${s.app_id}/requests?state=new`,
    })
  }
  const pendingNew = stats.filter((x) => !manual.has(x.app_id)).reduce((n, s) => n + s.pending_new, 0)
  if (pendingNew > 0) {
    out.push({ key: 'pending-requests', tone: 'publishing', text: `대기 중인 요청 ${pendingNew}건`, to: '/redeem/requests?state=new' })
  }
  for (const t of targets.filter((x) => x.status === 'active' && !x.voice?.persona)) {
    out.push({ key: `voice:${t.id}`, tone: 'publishing', text: `${t.name}: 말투가 없다`, to: '/targets' })
  }
  for (const it of scheduledDesk.filter((x) => x.platform === 'reddit' && !x.flair)) {
    out.push({
      key: `flair:${it.id}`,
      tone: 'publishing',
      text: `r/${it.channel} 예약 글에 플레어가 없다 — 플레어를 요구하는 서브레딧이면 게시가 막힌다`,
      to: `/redeem/posts/${it.id}`,
    })
  }
  return out.sort((a, b) => Number(b.tone === 'failed') - Number(a.tone === 'failed'))
}

/** What the attention strip knows that the list above does not: a desk job
 *  failing over and over, requests stuck in the queue, the paused Reddit
 *  watch, an unreadable health check. 오늘 draws no strip, so these join its
 *  own list instead and nothing the strip would say goes missing. */
const STRIP_ONLY = ['job:', 'stuck-requests', 'watch-paused', 'health-error']

export function stripNotes(items: AttentionItem[]): Note[] {
  return items
    .filter((i) => STRIP_ONLY.some((k) => i.key.startsWith(k)))
    .map((i) => ({
      key: `strip:${i.key}`,
      tone: i.tone,
      text: i.count > 0 ? `${i.label} ${i.count}건` : i.label,
      to: i.to,
    }))
}

const WEEKDAY_LONG = ['일요일', '월요일', '화요일', '수요일', '목요일', '금요일', '토요일']

/** "10월 17일 토요일" — the header line; the timeline already says 오늘. */
const longDay = (d: Date) => `${d.getMonth() + 1}월 ${d.getDate()}일 ${WEEKDAY_LONG[d.getDay()]}`

const midnight = (d: Date, plus = 0) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + plus)

export default function Today({ now: nowProp }: { now?: Date }) {
  const now = useMemo(() => nowProp ?? new Date(), [nowProp])
  const dayStart = midnight(now)
  const timelineQ = useMemo(
    () => ({ from: midnight(now).toISOString(), to: midnight(now, 2).toISOString() }),
    [now],
  )
  const feed = useSchedule(timelineQ, { live: true })
  const failed = useSchedule({ status: 'failed' }, { live: true })
  const publishing = useSchedule({ status: 'publishing' }, { live: true })
  const scheduledDesk = useSchedule({ status: 'scheduled', source: 'redeem' })
  const stats = useDeskStats(7)
  const apps = useRedeemApps()
  const requests = useRequests('all', 0, 1)
  const targets = useTargets()
  const drafts = useDraftCounts({}, true)
  const commands = usePostCommands()
  const attention = useAttentionItems()

  const items = feed.data?.items ?? []
  const appRows = apps.data?.items ?? []
  const statRows = stats.data?.items ?? []

  const today = dayStart.getTime()
  const todays = items.filter((it) => {
    const t = new Date(itemTime(it) ?? '').getTime()
    return t >= today && t < today + DAY_MS
  })
  const toGo = todays.filter((it) => ['scheduled', 'approved', 'publishing'].includes(it.status))
  const next = toGo
    .filter((it) => it.scheduled_at && new Date(it.scheduled_at).getTime() >= now.getTime())
    .sort((a, b) => String(a.scheduled_at).localeCompare(String(b.scheduled_at)))[0]

  const pendingNew = statRows.reduce((n, s) => n + s.pending_new, 0)
  const failedOpen = statRows.reduce((n, s) => n + s.failed_open, 0)
  const live = appRows.filter((r) => r.app.status === 'active')
  const lowest = live.slice().sort((a, b) => a.stats.available - b.stats.available)[0]
  const review = drafts.data?.counts?.pending_review ?? 0

  const notes = useMemo(
    () =>
      todayNotes({
        apps: appRows,
        stats: statRows,
        targets: targets.data ?? [],
        failed: failed.data?.items ?? [],
        publishing: publishing.data?.items ?? [],
        scheduledDesk: scheduledDesk.data?.items ?? [],
        now,
      })
        .concat(stripNotes(attention))
        .sort((a, b) => Number(b.tone === 'failed') - Number(a.tone === 'failed')),
    [appRows, statRows, targets.data, failed.data, publishing.data, scheduledDesk.data, now, attention],
  )

  return (
    <>
      <PageHeader title="오늘" description={longDay(now)} attention={false} />

      <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatTile
          label="오늘 나갈 글"
          value={toGo.length}
          hint={next ? `다음 ${formatShort(next.scheduled_at, now)} · ${channelLabel(next)}` : '남은 예약 없음'}
          to="/schedule"
        />
        <StatTile label="검토 대기" value={review} tone={review > 0 ? 'review' : undefined} hint="캠페인 글" to="/review" />
        <StatTile
          label="대기 요청"
          value={pendingNew}
          hint={failedOpen > 0 ? <span className="text-status-failed">실패 {failedOpen}건</span> : '실패 없음'}
          to="/redeem/requests"
        />
        <StatTile
          label="남은 코드 최저"
          value={lowest ? lowest.stats.available : '—'}
          tone={lowest ? (lowest.stats.available === 0 ? 'failed' : lowest.stats.available < LOW_POOL ? 'publishing' : undefined) : undefined}
          hint={lowest ? lowest.app.name : '가동 중인 앱 없음'}
          to={lowest ? `/redeem/${lowest.app.id}` : '/redeem'}
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_21rem]">
        <section aria-label="오늘과 내일" className="min-w-0 space-y-3 lg:order-first">
          <TimelineList
            label="오늘과 내일"
            items={items}
            now={now}
            getTime={itemTime}
            getKey={(it) => `${it.kind}-${it.id}`}
            renderItem={(it) => <ItemRow it={it} commands={commands} now={now} />}
            empty={
              feed.isLoading ? null : (
                <EmptyState
                  icon={CalendarClock}
                  title="오늘과 내일 잡힌 글이 없다"
                  description="앞으로 잡힌 글은 일정에서 본다."
                  actions={<ButtonLink to="/schedule">일정 열기</ButtonLink>}
                />
              )
            }
          />
        </section>

        <aside aria-label="확인할 것" className="order-first min-w-0 space-y-2 lg:order-none">
          <h2 className="flex items-baseline gap-2 text-table font-semibold">
            확인할 것
            <span className="tabular-nums text-muted-foreground">{notes.length}</span>
          </h2>
          {notes.length === 0 ? (
            <p className="rounded-lg border border-border bg-card px-3 py-3 text-table text-muted-foreground">
              지금 확인할 것이 없다.
            </p>
          ) : (
            <ul className="divide-y divide-border overflow-hidden rounded-lg border border-border bg-card">
              {notes.map((n) => (
                <li key={n.key}>
                  <Link to={n.to} className="flex items-start gap-2.5 px-3 py-2.5 text-table transition-colors hover:bg-muted/60">
                    <StatusDot tone={n.tone} className="mt-1.5" />
                    <NoteText text={n.text} />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </aside>
      </div>

      {commands.dialog}

      <DeskSection
        stats={statRows}
        apps={appRows}
        targets={targets.data ?? []}
        counts={requests.data?.counts ?? {}}
        loading={stats.isLoading}
      />
    </>
  )
}

/** A note's line. A failure's reason (after " — ") is the platform's own
 *  words, often a wall of JSON: it goes on a second, quieter line, cut at
 *  two lines, so the list stays a list. */
function NoteText({ text }: { text: string }) {
  const cut = text.indexOf(' — ')
  if (cut < 0) return <span className="min-w-0 break-words">{text}</span>
  return (
    <span className="min-w-0">
      <span className="block break-words">{text.slice(0, cut)}</span>
      <span className="mt-0.5 line-clamp-2 break-all font-mono text-micro text-muted-foreground">{text.slice(cut + 3)}</span>
    </span>
  )
}

const REQUEST_ORDER = ['replied', 'sent', 'new', 'duplicate', 'ignored', 'failed'] as const

/** The desk's day per app, closed until opened. */
function DeskSection({
  stats,
  apps,
  targets,
  counts,
  loading,
}: {
  stats: DeskStat[]
  apps: RedeemAppWithStats[]
  targets: Target[]
  counts: Record<string, number>
  loading: boolean
}) {
  const [shown, setShown] = useState(false)
  const issuedToday = stats.reduce((n, s) => n + s.issued_today, 0)
  const issuedWindow = stats.reduce((n, s) => n + s.issued_window, 0)
  const live = apps.filter((r) => r.app.status === 'active').length
  const auto = apps.filter((r) => r.app.status === 'active' && r.app.auto_handout === 1).length

  const columns: Column<DeskStat>[] = [
    {
      key: 'app',
      header: '앱',
      card: 'title',
      cell: (s) => {
        const app = apps.find((r) => r.app.id === s.app_id)?.app
        const st = statusOf('app', app?.status ?? '')
        return (
          <span className="flex flex-wrap items-center gap-x-2">
            <Link to={`/redeem/${s.app_id}`} className="font-medium hover:underline" onClick={(e) => e.stopPropagation()}>
              {s.name}
            </Link>
            {app && (
              <span className="text-meta font-normal text-muted-foreground">
                {st.label}
                {app.status === 'active' && (
                  <>
                    {' · '}
                    {s.auto_handout === 1 ? (
                      '자동 발송'
                    ) : (
                      <span className={cn('font-medium', TONE_CLASS.publishing.text)}>수동 발송</span>
                    )}
                  </>
                )}
              </span>
            )}
          </span>
        )
      },
    },
    {
      key: 'available',
      header: '남은 코드',
      align: 'right',
      className: 'tabular-nums',
      cell: (s) => <span className={cn(s.available === 0 && 'font-medium text-status-failed')}>{s.available}</span>,
    },
    {
      key: 'today',
      header: '오늘 발급 / 요청',
      align: 'right',
      className: 'tabular-nums',
      cell: (s) => `${s.issued_today} / ${s.requests_today}`,
    },
    {
      key: 'pending',
      header: '대기',
      align: 'right',
      className: 'tabular-nums',
      cell: (s) => <span className={cn(s.pending_new > 0 && 'font-medium text-status-publishing')}>{s.pending_new}</span>,
    },
    {
      key: 'last',
      header: '마지막 발급',
      className: 'whitespace-nowrap text-muted-foreground',
      cell: (s) => <When iso={s.last_issued_at} />,
    },
  ]

  return (
    <section aria-label="데스크와 계정" className="mt-8 space-y-3">
      <button
        type="button"
        onClick={() => setShown((v) => !v)}
        aria-expanded={shown}
        className="flex w-full items-center gap-2 text-left text-table font-semibold hover:underline"
      >
        <ChevronDown size={16} aria-hidden className={cn('transition-transform', !shown && '-rotate-90')} />
        데스크와 계정
        <span className="text-meta font-normal text-muted-foreground">
          오늘 {issuedToday}장 발급 · 최근 7일 {issuedWindow}장 · 가동 {live}/{apps.length}개 · 자동 발송 {auto}개
        </span>
      </button>
      {shown && (
        <div className="space-y-4">
          <DataTable
            label="앱별 데스크"
            columns={columns}
            rows={stats}
            rowKey={(s) => s.app_id}
            loading={loading}
            empty={<EmptyState title="아직 데스크 활동이 없다" />}
          />
          <div className="grid gap-4 lg:grid-cols-2">
            <div className="space-y-2 rounded-lg border border-border bg-card p-4">
              <h3 className="text-table font-semibold">요청 처리 현황</h3>
              <dl className="flex flex-wrap gap-x-5 gap-y-1.5 text-table">
                {REQUEST_ORDER.map((k) => (
                  <div key={k} className="flex items-baseline gap-1.5">
                    <dt className="text-muted-foreground">{statusOf('request', k).label}</dt>
                    <dd className={cn('font-semibold tabular-nums', k === 'failed' && (counts[k] ?? 0) > 0 && 'text-status-failed')}>
                      {counts[k] ?? 0}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
            <div className="space-y-2 rounded-lg border border-border bg-card p-4">
              <h3 className="flex items-baseline justify-between text-table font-semibold">
                계정
                <Link to="/targets" className="text-meta font-normal text-accent hover:underline">
                  말투 관리
                </Link>
              </h3>
              {targets.length === 0 ? (
                <p className="text-table text-muted-foreground">연결된 계정이 없다.</p>
              ) : (
                <ul className="space-y-1.5 text-table">
                  {targets.map((t) => (
                    <li key={t.id} className="flex items-center gap-2">
                      <PlatformMark platform={t.platform} />
                      <span className="font-medium">{t.name}</span>
                      <span
                        className={cn('min-w-0 flex-1 truncate text-right text-meta', t.voice?.persona ? 'text-muted-foreground' : 'text-status-publishing')}
                        title={t.voice?.persona}
                      >
                        {t.voice?.persona ? t.voice.persona.split('\n')[0] : '말투 없음'}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  )
}
